<template>
    <div
        class="bo-search-suggestions"
        :class="{ 'pinned-view-all': pinViewAll }"
    >
        <div
            class="search-suggestions-results"
            @scroll.passive="$emit('results-scroll')"
            @touchmove.passive="$emit('results-scroll')"
        >
            <div
                v-if="showCategorySection"
                class="search-section"
            >
                <div class="search-section-title">
                    {{ headerTranslations?.categories ?? "Categories" }}
                    <template v-if="!categoriesLoading">({{ categoryTotal }})</template>
                </div>
                <template v-if="categoriesLoading">
                    <div
                        v-for="n in 2"
                        :key="`category-skeleton-${n}`"
                        class="search-suggestion search-skeleton"
                    >
                        <div class="suggestion-text">
                            <div class="skeleton-line short"></div>
                            <div class="skeleton-line"></div>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div
                        v-for="category in visibleCategories"
                        :key="`category-${category.id}`"
                        class="search-suggestion category"
                        @click="$emit('select-category', category)"
                    >
                        <div class="suggestion-text">
                            <span class="count">{{ productCountText(category.product_count) }}</span>
                            <span
                                class="name"
                                v-html="highlight(category.name)"
                            ></span>
                        </div>
                    </div>
                </template>
            </div>

            <div
                v-if="showProductSection"
                class="search-section"
            >
                <div class="search-section-title">
                    {{ headerTranslations?.products ?? "Products" }}
                    <template v-if="!productsLoading">({{ productTotal }})</template>
                </div>
                <template v-if="productsLoading">
                    <div
                        v-for="n in 3"
                        :key="`product-skeleton-${n}`"
                        class="search-suggestion search-skeleton"
                    >
                        <div class="suggestion-image skeleton-block"></div>
                        <div class="suggestion-text">
                            <div class="skeleton-line"></div>
                            <div class="skeleton-line short"></div>
                            <div class="skeleton-line short"></div>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div
                        v-for="product in visibleProducts"
                        :key="`product-${product.id}`"
                        class="search-suggestion product"
                        @click="$emit('select-product', product)"
                    >
                        <div class="suggestion-image">
                            <img
                                :src="product.image || placeholderImage"
                                @error="showPlaceholderImage"
                            />
                        </div>
                        <div class="suggestion-text">
                            <span
                                class="name"
                                v-html="highlight(product.name)"
                            ></span>
                            <span
                                v-if="product.sku"
                                class="detail"
                            >
                                <span class="detail-label">{{ headerTranslations?.sku ?? "SKU" }}</span>
                                <span v-html="highlight(product.sku)"></span>
                            </span>
                            <span
                                v-if="product.barcode"
                                class="detail"
                            >
                                <span class="detail-label">{{ headerTranslations?.barcode ?? "Barcode" }}</span>
                                <span v-html="highlight(product.barcode)"></span>
                            </span>
                            <span
                                v-if="showDescription && descriptionOf(product)"
                                class="detail description"
                            >
                                <span class="detail-label">{{ headerTranslations?.description ?? "Description" }}</span>
                                <span
                                    class="detail-value"
                                    v-html="highlight(descriptionOf(product))"
                                ></span>
                            </span>
                        </div>
                    </div>
                </template>
            </div>

            <div
                v-if="noResults"
                class="search-empty"
            >
                {{ generalTranslations.no_results_found ?? "No results found" }}
            </div>

            <button
                v-if="!pinViewAll && !noResults"
                type="button"
                class="search-view-all"
                @click="$emit('view-all')"
            >
                {{ viewAllText }} <i class="icon-chevronright"></i>
            </button>
        </div>

        <button
            v-if="pinViewAll && !noResults"
            type="button"
            class="search-view-all"
            @click="$emit('view-all')"
        >
            {{ viewAllText }} <i class="icon-chevronright"></i>
        </button>
    </div>
</template>

<script>
import { highlightMatches, descriptionSnippet } from "js/frontend/utils/search-text.js";
import { PRODUCT_PLACEHOLDER_IMAGE, showPlaceholderImage } from "js/frontend/utils/product-image.js";

const MAX_SUGGESTIONS = 6;
const MAX_CATEGORY_SUGGESTIONS = 2;

export default {
    props: {
        query: { type: String, default: "" },
        words: { type: Array, default: () => [] },
        showProducts: { type: Boolean, default: true },
        // false while the shown results are still for an earlier query
        resultsCurrent: { type: Boolean, default: true },
        showCategories: { type: Boolean, default: false },
        showDescription: { type: Boolean, default: false },
        products: { type: Array, default: () => [] },
        productTotal: { type: Number, default: 0 },
        productsLoading: { type: Boolean, default: false },
        categories: { type: Array, default: () => [] },
        categoryTotal: { type: Number, default: 0 },
        categoriesLoading: { type: Boolean, default: false },
        // desktop keeps View All visible below a scrolling list; mobile puts it last in the list
        pinViewAll: { type: Boolean, default: false },
        headerTranslations: { type: Object, default: null },
    },

    data() {
        return {
            placeholderImage: PRODUCT_PLACEHOLDER_IMAGE,
            generalTranslations: window.bo?.generalTranslations ?? {},
        };
    },

    computed: {
        showCategorySection() {
            return this.showCategories && (this.categoriesLoading || this.categoryTotal > 0);
        },

        showProductSection() {
            return this.showProducts && (this.productsLoading || this.productTotal > 0);
        },

        noResults() {
            return this.resultsCurrent && !this.showCategorySection && !this.showProductSection;
        },

        // at most 2 categories unless products leave spare slots
        visibleCategories() {
            if (!this.showCategories) return [];

            // products from an earlier search stay in `products` when no product chip is on
            const productSlots = !this.showProducts
                ? 0
                : this.productsLoading
                  ? MAX_SUGGESTIONS - MAX_CATEGORY_SUGGESTIONS
                  : this.products.length;
            const limit = Math.max(MAX_CATEGORY_SUGGESTIONS, MAX_SUGGESTIONS - productSlots);

            return this.categories.slice(0, limit);
        },

        visibleProducts() {
            const categorySlots =
                this.showCategories && this.categoriesLoading
                    ? MAX_CATEGORY_SUGGESTIONS
                    : this.visibleCategories.length;

            return this.products.slice(0, MAX_SUGGESTIONS - categorySlots);
        },

        viewAllText() {
            const template = this.headerTranslations?.view_all_results_for ?? 'View all results for ":object"';

            return template.replace(":object", this.query);
        },
    },

    methods: {
        showPlaceholderImage,

        highlight(text) {
            return highlightMatches(text, this.words);
        },

        descriptionOf(product) {
            return descriptionSnippet(product.description, this.words, 120);
        },

        productCountText(count) {
            const template = this.headerTranslations?.products_count ?? ":count Products";

            return template.replace(":count", count ?? 0);
        },
    },
};
</script>
